/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as components from "../components/index.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

export type RetrieveFeesGlobals = {
  /**
   * Specify an API version.
   *
   * @remarks
   *
   * API versioning follows the format `vYYYY.QQ.BB`, where
   *   - `YYYY` is the year
   *   - `QQ` is the two-digit month for the first month of the quarter (e.g., 01, 04, 07, 10)
   *   - `BB` is the build number, starting at `.01`, for subsequent builds in the same quarter.
   *     - For example, `v2024.01.00` is the initial release of the first quarter of 2024.
   *
   * The `latest` version represents the most recent development state. It may include breaking changes and should be treated as a beta release.
   * When no version is specified, the API defaults to `v2024.01.00`.
   */
  xMoovVersion?: string | undefined;
};

export type RetrieveFeesRequest = {
  accountID: string;
  /**
   * Optional transfer ID to filter the results by.
   */
  transferID?: string | undefined;
  /**
   * Optional dispute ID to filter the results by.
   */
  disputeID?: string | undefined;
  /**
   * Optional residual ID to filter the results by.
   */
  residualID?: string | undefined;
  /**
   * Optional date-time to inclusively filter all fees created after this date-time.
   */
  startDateTime?: string | undefined;
  /**
   * Optional date-time to exclusively filter all fees created before this date-time.
   */
  endDateTime?: string | undefined;
  skip?: number | undefined;
  count?: number | undefined;
};

export type RetrieveFeesResponse = {
  headers: { [k: string]: Array<string> };
  result: Array<components.IncurredFee>;
};

/** @internal */
export const RetrieveFeesGlobals$inboundSchema: z.ZodType<
  RetrieveFeesGlobals,
  z.ZodTypeDef,
  unknown
> = z.object({
  "X-Moov-Version": z.string().optional(),
}).transform((v) => {
  return remap$(v, {
    "X-Moov-Version": "xMoovVersion",
  });
});
/** @internal */
export type RetrieveFeesGlobals$Outbound = {
  "X-Moov-Version"?: string | undefined;
};

/** @internal */
export const RetrieveFeesGlobals$outboundSchema: z.ZodType<
  RetrieveFeesGlobals$Outbound,
  z.ZodTypeDef,
  RetrieveFeesGlobals
> = z.object({
  xMoovVersion: z.string().optional(),
}).transform((v) => {
  return remap$(v, {
    xMoovVersion: "X-Moov-Version",
  });
});

export function retrieveFeesGlobalsToJSON(
  retrieveFeesGlobals: RetrieveFeesGlobals,
): string {
  return JSON.stringify(
    RetrieveFeesGlobals$outboundSchema.parse(retrieveFeesGlobals),
  );
}
export function retrieveFeesGlobalsFromJSON(
  jsonString: string,
): SafeParseResult<RetrieveFeesGlobals, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RetrieveFeesGlobals$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RetrieveFeesGlobals' from JSON`,
  );
}

/** @internal */
export const RetrieveFeesRequest$inboundSchema: z.ZodType<
  RetrieveFeesRequest,
  z.ZodTypeDef,
  unknown
> = z.object({
  accountID: z.string(),
  transferID: z.string().optional(),
  disputeID: z.string().optional(),
  residualID: z.string().optional(),
  startDateTime: z.string().optional(),
  endDateTime: z.string().optional(),
  skip: z.number().int().optional(),
  count: z.number().int().optional(),
});
/** @internal */
export type RetrieveFeesRequest$Outbound = {
  accountID: string;
  transferID?: string | undefined;
  disputeID?: string | undefined;
  residualID?: string | undefined;
  startDateTime?: string | undefined;
  endDateTime?: string | undefined;
  skip?: number | undefined;
  count?: number | undefined;
};

/** @internal */
export const RetrieveFeesRequest$outboundSchema: z.ZodType<
  RetrieveFeesRequest$Outbound,
  z.ZodTypeDef,
  RetrieveFeesRequest
> = z.object({
  accountID: z.string(),
  transferID: z.string().optional(),
  disputeID: z.string().optional(),
  residualID: z.string().optional(),
  startDateTime: z.string().optional(),
  endDateTime: z.string().optional(),
  skip: z.number().int().optional(),
  count: z.number().int().optional(),
});

export function retrieveFeesRequestToJSON(
  retrieveFeesRequest: RetrieveFeesRequest,
): string {
  return JSON.stringify(
    RetrieveFeesRequest$outboundSchema.parse(retrieveFeesRequest),
  );
}
export function retrieveFeesRequestFromJSON(
  jsonString: string,
): SafeParseResult<RetrieveFeesRequest, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RetrieveFeesRequest$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RetrieveFeesRequest' from JSON`,
  );
}

/** @internal */
export const RetrieveFeesResponse$inboundSchema: z.ZodType<
  RetrieveFeesResponse,
  z.ZodTypeDef,
  unknown
> = z.object({
  Headers: z.record(z.array(z.string())).default({}),
  Result: z.array(components.IncurredFee$inboundSchema),
}).transform((v) => {
  return remap$(v, {
    "Headers": "headers",
    "Result": "result",
  });
});
/** @internal */
export type RetrieveFeesResponse$Outbound = {
  Headers: { [k: string]: Array<string> };
  Result: Array<components.IncurredFee$Outbound>;
};

/** @internal */
export const RetrieveFeesResponse$outboundSchema: z.ZodType<
  RetrieveFeesResponse$Outbound,
  z.ZodTypeDef,
  RetrieveFeesResponse
> = z.object({
  headers: z.record(z.array(z.string())),
  result: z.array(components.IncurredFee$outboundSchema),
}).transform((v) => {
  return remap$(v, {
    headers: "Headers",
    result: "Result",
  });
});

export function retrieveFeesResponseToJSON(
  retrieveFeesResponse: RetrieveFeesResponse,
): string {
  return JSON.stringify(
    RetrieveFeesResponse$outboundSchema.parse(retrieveFeesResponse),
  );
}
export function retrieveFeesResponseFromJSON(
  jsonString: string,
): SafeParseResult<RetrieveFeesResponse, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => RetrieveFeesResponse$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'RetrieveFeesResponse' from JSON`,
  );
}
